import type {
  AcompanhamentoDto,
  AlterarNotaEntrada,
  CriarConectorEntrada,
  CriarNotaProcessoEntrada,
  CriarNotaQuadroEntrada,
  EnvioDiretoDto,
  NotasQuadroDto,
  UrlArquivoDto,
} from '@nexital/shared';
import type { ApiNotas, Criado } from '../application/portas-notas';
import { enviarDiretoAoS3 } from './envio-s3';

export type Chamar = <T>(metodo: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE', caminho: string, corpo?: unknown) => Promise<T>;

const quadro = (uuid: string) => `/api/quadros/${uuid}`;

/** Rotas de notas e setas do servidor (apps/api/src/presentation/http/rotas/notas.ts). */
export class ApiNotasHttp implements ApiNotas {
  constructor(private readonly chamar: Chamar) {}

  listarDoQuadro(quadroUuid: string) {
    return this.chamar<NotasQuadroDto>('GET', `${quadro(quadroUuid)}/notas`);
  }

  criarNota(quadroUuid: string, entrada: CriarNotaQuadroEntrada) {
    return this.chamar<Criado>('POST', `${quadro(quadroUuid)}/notas`, entrada);
  }

  alterarNota(quadroUuid: string, notaUuid: string, entrada: AlterarNotaEntrada) {
    return this.chamar<{ versao: number }>('PATCH', `${quadro(quadroUuid)}/notas/${notaUuid}`, entrada);
  }

  async excluirNota(quadroUuid: string, notaUuid: string) {
    await this.chamar<unknown>('DELETE', `${quadro(quadroUuid)}/notas/${notaUuid}`);
  }

  criarSeta(quadroUuid: string, entrada: CriarConectorEntrada) {
    return this.chamar<Criado>('POST', `${quadro(quadroUuid)}/conectores`, entrada);
  }

  async excluirSeta(quadroUuid: string, setaUuid: string) {
    await this.chamar<unknown>('DELETE', `${quadro(quadroUuid)}/conectores/${setaUuid}`);
  }

  listarDoProcesso(quadroUuid: string, processoUuid: string) {
    return this.chamar<NotasQuadroDto>('GET', `${quadro(quadroUuid)}/processos/${processoUuid}/notas`);
  }

  criarNotaProcesso(quadroUuid: string, processoUuid: string, entrada: Partial<CriarNotaProcessoEntrada>) {
    return this.chamar<Criado>('POST', `${quadro(quadroUuid)}/processos/${processoUuid}/notas`, entrada);
  }

  criarSetaProcesso(quadroUuid: string, processoUuid: string, entrada: CriarConectorEntrada) {
    return this.chamar<Criado>('POST', `${quadro(quadroUuid)}/processos/${processoUuid}/conectores`, entrada);
  }

  obterAcompanhamento(quadroUuid: string) {
    return this.chamar<AcompanhamentoDto>('GET', `${quadro(quadroUuid)}/acompanhamento`);
  }

  async anexar(quadroUuid: string, notaUuid: string, arquivo: File) {
    const base = `${quadro(quadroUuid)}/notas/${notaUuid}/anexos`;
    const referencia = await enviarDiretoAoS3(arquivo, (entrada) => this.chamar<EnvioDiretoDto>('POST', `${base}/envio`, entrada));
    await this.chamar<unknown>('POST', `${base}/${referencia}/confirmar`);
  }

  urlAnexo(quadroUuid: string, notaUuid: string, arquivoUuid: string) {
    return this.chamar<UrlArquivoDto>('GET', `${quadro(quadroUuid)}/notas/${notaUuid}/anexos/${arquivoUuid}`);
  }

  async excluirAnexo(quadroUuid: string, notaUuid: string, arquivoUuid: string) {
    await this.chamar<unknown>('DELETE', `${quadro(quadroUuid)}/notas/${notaUuid}/anexos/${arquivoUuid}`);
  }

  async definirAcompanhamento(quadroUuid: string, acompanhar: string[]) {
    await this.chamar<unknown>('PUT', `${quadro(quadroUuid)}/acompanhamento`, { acompanhar });
  }
}
